/**
 * Stock History Dialog Component
 */

import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Loader2, TrendingUp } from "lucide-react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { formatCurrency } from "@/lib/utils";
import { estoqueAdapter } from "@/data";
import type { StockItem, MovementHistory } from "@/types/stock";

interface StockHistoryDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  product: StockItem | null;
}

export function StockHistoryDialog({
  open,
  onOpenChange,
  product,
}: StockHistoryDialogProps) {
  const [history, setHistory] = useState<MovementHistory[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (open && product) {
      loadHistory();
    }
  }, [open, product]);

  const loadHistory = async () => {
    if (!product) return;

    setIsLoading(true);
    try {
      const data = await estoqueAdapter.getHistory(product.produto_id);
      setHistory(data);
    } catch (error) {
      console.error("Erro ao carregar histórico:", error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[80vh] flex flex-col overflow-hidden">
        <DialogHeader className="flex-shrink-0 pb-4">
          <DialogTitle className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5" />
            Histórico de Movimentações
          </DialogTitle>
          <DialogDescription>
            {product && (
              <>
                <span className="font-semibold">{product.produto_nome}</span>
                <div className="mt-1 text-xs">
                  Estoque atual: {Number(product.quantidade_atual).toFixed(2)}
                </div>
              </>
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="overflow-y-auto flex-1 -mx-6 px-6">
        <div className="px-6">
        {isLoading ? (
          <div className="flex justify-center items-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : history.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            Nenhuma movimentação encontrada para este produto.
          </div>
        ) : (
          <div className="border rounded-lg">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Data</TableHead>
                  <TableHead>Tipo</TableHead>
                  <TableHead>Fornecedor</TableHead>
                  <TableHead>Documento</TableHead>
                  <TableHead className="text-right">Quantidade</TableHead>
                  <TableHead className="text-right">Custo Unit.</TableHead>
                  <TableHead>Lote</TableHead>
                  <TableHead>Validade</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {history.map((item) => (
                  <TableRow key={item.entrada_item_id}>
                    <TableCell className="text-sm">
                      {format(
                        new Date(item.entrada.data_entrada),
                        "dd/MM/yyyy",
                        { locale: ptBR }
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">
                        {item.entrada.tipo_movimento}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm">
                      {item.entrada.fornecedor_nome || "—"}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {item.entrada.documento_tipo}{" "}
                      {item.entrada.documento_numero || "—"}
                    </TableCell>
                    <TableCell className="text-right font-mono">
                      +{Number(item.quantidade_estoque_calculada).toFixed(3)}{" "}
                      <span className="text-xs text-muted-foreground">
                        {item.tipo_embalagem || "un"}
                        {item.peso_individual_g && ` (${item.peso_individual_g}g)`}
                        {item.volume_individual_ml && ` (${item.volume_individual_ml}ml)`}
                      </span>
                    </TableCell>
                    <TableCell className="text-right font-mono text-sm">
                      {formatCurrency(Number(item.custo_unitario_final_base))}
                    </TableCell>
                    <TableCell className="text-xs">{item.lote || "—"}</TableCell>
                    <TableCell className="text-xs">
                      {item.validade
                        ? format(new Date(item.validade), "dd/MM/yyyy", {
                            locale: ptBR,
                          })
                        : "—"}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
        </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
